import React, { useCallback, useEffect, useRef, useState } from "react";

import { useForegroundRefresh } from "../hooks/useForegroundRefresh";
import {
  api,
  type KnowledgeItemData,
} from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";
import { updateTrackedKnowledgeBackgroundItem } from "../lib/knowledgeBackground";
import { uploadKnowledgePdf } from "../lib/knowledgePdfUpload";
import { storage } from "../lib/storage";
import {
  getKnowledgeFailureDisplayMessage,
  getKnowledgeItemDisplayName,
  getKnowledgeLibraryStatusLabel,
  getKnowledgeStatusLabel,
  getKnowledgeSummaryStatusLabel,
  getKnowledgeTypeLabel,
  getKnowledgeStructureLabel,
  getKnowledgeUnavailableReason,
  isKnowledgeItemCompanyResearchUsable,
  isKnowledgeItemGroupTalkUsable,
  isKnowledgeItemPollingStatus,
  isKnowledgeItemUsable,
  shouldShowKnowledgeSummaryStatus,
  validateKnowledgePdfFile,
} from "../lib/knowledge";

export type KnowledgeAttachmentPickerMode = "default" | "groupTalk" | "companyResearch";

type KnowledgeAttachmentPickerSharedProps = {
  mode?: KnowledgeAttachmentPickerMode;
  allowedTypes?: readonly KnowledgeItemData["type"][];
  showUpload?: boolean;
  allowDelete?: boolean;
  compact?: boolean;
  heading?: string;
  description?: string;
  usageNote?: React.ReactNode;
  emptyMessage?: string;
  selectedLabel?: string;
  detachLabel?: string;
  pollingMessage?: string;
  unavailableFallbackMessage?: string;
};

type SingleKnowledgeAttachmentPickerProps = KnowledgeAttachmentPickerSharedProps & {
  mode?: Exclude<KnowledgeAttachmentPickerMode, "groupTalk">;
  value: KnowledgeItemData | null;
  onChange: (item: KnowledgeItemData | null) => void;
};

type GroupTalkKnowledgeAttachmentPickerProps = KnowledgeAttachmentPickerSharedProps & {
  mode: "groupTalk";
  value: KnowledgeItemData[];
  onChange: (items: KnowledgeItemData[]) => void;
  selectionLimit: number;
};

type KnowledgeAttachmentPickerProps =
  | SingleKnowledgeAttachmentPickerProps
  | GroupTalkKnowledgeAttachmentPickerProps;

const POLLING_INTERVAL_MS = 2000;
const MAX_POLLING_ATTEMPTS = 30;
const DEFAULT_PICKER_KNOWLEDGE_TYPES = ["pdf", "executive_memo"] as const;
const GROUP_TALK_PICKER_KNOWLEDGE_TYPES = [
  ...DEFAULT_PICKER_KNOWLEDGE_TYPES,
  "url",
  "company_research",
  "deep_report",
] as const;

function pickerAllowedTypes(mode: KnowledgeAttachmentPickerMode): readonly string[] {
  return mode === "groupTalk" ? GROUP_TALK_PICKER_KNOWLEDGE_TYPES : DEFAULT_PICKER_KNOWLEDGE_TYPES;
}

function isPickerItemUsable(item: KnowledgeItemData, mode: KnowledgeAttachmentPickerMode): boolean {
  if (mode === "groupTalk") return isKnowledgeItemGroupTalkUsable(item);
  if (mode === "companyResearch") return isKnowledgeItemCompanyResearchUsable(item);
  return isKnowledgeItemUsable(item);
}

function getPickerKnowledgeStatusLabel(
  item: KnowledgeItemData,
  mode: KnowledgeAttachmentPickerMode
): string {
  return (mode === "groupTalk" || mode === "companyResearch") && item.type === "url"
    ? getKnowledgeLibraryStatusLabel(item)
    : getKnowledgeStatusLabel(item.status);
}

function mergeLibraryItems(first: KnowledgeItemData[], rest: KnowledgeItemData[]): KnowledgeItemData[] {
  const seen = new Set<string>();
  return [...first, ...rest].filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function KnowledgeAttachmentPicker(props: KnowledgeAttachmentPickerProps) {
  const {
    value,
    onChange,
    mode = "default",
    allowedTypes: explicitAllowedTypes,
    showUpload = true,
    allowDelete,
    compact = false,
    heading,
    description,
    usageNote,
    emptyMessage = "参考資料は未選択です。ナレッジなしでも壁打ちは始められます。",
    selectedLabel = "添付中のナレッジ",
    detachLabel = "この相談から外す",
    pollingMessage = "ナレッジを読み取り中です。完了すると、このナレッジを前提に壁打ちできます。",
    unavailableFallbackMessage = "このナレッジはまだ利用できません。利用可能になってから開始できます。",
  } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingAbortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const onChangeRef = useRef(onChange);

  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryNextCursor, setLibraryNextCursor] = useState<string | null>(null);
  const [libraryLoadingMore, setLibraryLoadingMore] = useState(false);
  const libraryRequestPendingRef = useRef(false);
  const [libraryItems, setLibraryItems] = useState<KnowledgeItemData[]>([]);
  const multiple = mode === "groupTalk";
  const selectionLimitCandidate = props.mode === "groupTalk" ? props.selectionLimit : null;
  const selectionLimit = multiple && Number.isInteger(selectionLimitCandidate) && (selectionLimitCandidate ?? 0) > 0
    ? selectionLimitCandidate
    : null;
  const selectedItems = (multiple ? value : value ? [value] : []) as KnowledgeItemData[];
  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;
  const deleteAllowed = multiple ? false : allowDelete ?? true;
  const selectionLimitReached = multiple && (selectionLimit === null || selectedItems.length >= selectionLimit);
  const renderedHeading = heading ?? (multiple ? "この壁打ちで使う資料" : "参考資料");
  const allowedTypes = explicitAllowedTypes ?? pickerAllowedTypes(mode);
  const allowedTypesKey = allowedTypes.join(",");
  const allowedTypeSet = new Set<string>(allowedTypes);
  const allowsUrl = allowedTypeSet.has("url");
  const allowsDeepReport = allowedTypeSet.has("deep_report");
  const reportSourceLabel = allowsDeepReport ? "、深掘りレポート" : "";
  const allowsCompanyResearch = allowedTypeSet.has("company_research");
  const renderedDescription = description ?? (
    multiple
      ? selectionLimit === null
        ? "選択条件を確認できないため、参考資料を追加できません。"
        : `PDF、Webページ、社長メモ、企業リサーチ${reportSourceLabel}を最大${selectionLimit}件まで選択できます。`
      : allowsCompanyResearch
        ? `PDF、Webページ、社長メモ、企業リサーチ${reportSourceLabel}を1件添付できます。選び直すと差し替わります。`
        : allowsUrl
          ? "PDF、Webページ、または社長メモを1件添付できます。選び直すと差し替わります。"
          : "PDFまたは社長メモを1件添付できます。選び直すと差し替わります。"
  );
  const pollingItemId = selectedItems.find((item) => isKnowledgeItemPollingStatus(item.status))?.id ?? "";

  const emitItems = useCallback((items: KnowledgeItemData[]) => {
    selectedItemsRef.current = items;
    if (multiple) {
      (onChangeRef.current as GroupTalkKnowledgeAttachmentPickerProps["onChange"])(items);
      return;
    }
    (onChangeRef.current as SingleKnowledgeAttachmentPickerProps["onChange"])(items[0] ?? null);
  }, [multiple]);

  const replaceSelectedItem = useCallback((item: KnowledgeItemData) => {
    if (!multiple) {
      emitItems([item]);
      return;
    }
    const current = selectedItemsRef.current;
    const existingIndex = current.findIndex((entry) => entry.id === item.id);
    if (existingIndex >= 0) {
      emitItems(current.map((entry, index) => index === existingIndex ? item : entry));
      return;
    }
    if (selectionLimit !== null && current.length < selectionLimit) {
      emitItems([...current, item]);
    }
  }, [emitItems, multiple, selectionLimit]);

  const removeSelectedItem = useCallback((itemId: string) => {
    if (!multiple) {
      emitItems([]);
      return;
    }
    const current = selectedItemsRef.current;
    emitItems(current.filter((item) => item.id !== itemId));
  }, [emitItems, multiple]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current !== null) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    if (pollingAbortRef.current !== null) {
      pollingAbortRef.current.abort();
      pollingAbortRef.current = null;
    }
  }, []);

  const pollKnowledgeItem = useCallback((itemId: string, attempt = 1) => {
    stopPolling();
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    pollingAbortRef.current = controller;

    api.getKnowledgeItem(itemId, controller?.signal)
      .then((item) => {
        if (!mountedRef.current) return;
        replaceSelectedItem(item);
        updateTrackedKnowledgeBackgroundItem(storage.getAuth().userId, item);
        if (item.status === "ready" || item.status === "failed" || item.status === "deleted") {
          stopPolling();
          return;
        }
        if (attempt >= MAX_POLLING_ATTEMPTS) {
          setMessage("読み取り完了を確認できませんでした。少し時間を置いてナレッジライブラリから確認してください。");
          stopPolling();
          return;
        }
        pollingTimeoutRef.current = setTimeout(() => pollKnowledgeItem(itemId, attempt + 1), POLLING_INTERVAL_MS);
      })
      .catch((error) => {
        if (!mountedRef.current || controller?.signal.aborted) return;
        logError(error);
        setMessage(`${toUserMessage(error)} 読み取り状況を確認できませんでした。`);
        stopPolling();
      });
  }, [replaceSelectedItem, stopPolling]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (pollingItemId) {
      pollKnowledgeItem(pollingItemId);
      return;
    }
    stopPolling();
  }, [pollKnowledgeItem, pollingItemId, stopPolling]);

  const handleUploadClick = () => {
    if (selectionLimitReached) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    event.currentTarget.value = "";
    if (!file || uploading || selectionLimitReached) return;

    const validationError = validateKnowledgePdfFile(file);
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setUploading(true);
    setMessage("");
    stopPolling();

    let knowledgeItemId = "";
    try {
      await uploadKnowledgePdf(file, {
        userId: storage.getAuth().userId,
        onItemChange: (item) => {
          knowledgeItemId = item.id;
          replaceSelectedItem(item);
        },
      });
    } catch (error) {
      logError(error);
      if (knowledgeItemId) {
        removeSelectedItem(knowledgeItemId);
      }
      setMessage(`${toUserMessage(error)} もう一度アップロードできます。`);
    } finally {
      if (mountedRef.current) {
        setUploading(false);
      }
    }
  };

  const refreshLibrary = useCallback(async (silent: boolean) => {
    if (libraryRequestPendingRef.current) return;
    libraryRequestPendingRef.current = true;
    if (!silent) {
      setLibraryLoading(true);
      setMessage("");
    }
    try {
      const response = await api.listKnowledgeItems({ type: allowedTypesKey, limit: 20 });
      const allowed = new Set(allowedTypesKey.split(","));
      if (!mountedRef.current) return;
      const firstPage = response.items.filter(item => allowed.has(item.type) && item.status !== "deleted");
      setLibraryItems(current => mergeLibraryItems(firstPage, current));
      setLibraryNextCursor(response.next_cursor ?? null);
    } catch (error) {
      logError(error);
      if (!silent) {
        setMessage(`${toUserMessage(error)} ナレッジライブラリを読み込めませんでした。`);
      }
    } finally {
      libraryRequestPendingRef.current = false;
      if (mountedRef.current && !silent) setLibraryLoading(false);
    }
  }, [allowedTypesKey]);

  useForegroundRefresh(
    () => refreshLibrary(true),
    { enabled: libraryOpen }
  );

  const loadMoreLibrary = async () => {
    if (!libraryNextCursor || libraryRequestPendingRef.current) return;
    libraryRequestPendingRef.current = true;
    setLibraryLoadingMore(true);
    setMessage("");
    try {
      const response = await api.listKnowledgeItems({ type: allowedTypesKey, limit: 20, cursor: libraryNextCursor });
      if (!mountedRef.current) return;
      const allowed = new Set(allowedTypesKey.split(","));
      const additional = response.items.filter(item => allowed.has(item.type) && item.status !== "deleted");
      setLibraryItems(current => mergeLibraryItems(current, additional));
      setLibraryNextCursor(response.next_cursor ?? null);
    } catch (error) {
      logError(error);
      if (mountedRef.current) setMessage(toUserMessage(error) + " ナレッジライブラリの続きを読み込めませんでした。");
    } finally {
      libraryRequestPendingRef.current = false;
      if (mountedRef.current) setLibraryLoadingMore(false);
    }
  };

  const loadLibrary = async () => {
    setLibraryOpen(true);
    await refreshLibrary(false);
  };

  const selectExistingItem = (item: KnowledgeItemData) => {
    const selected = selectedItems.some((entry) => entry.id === item.id);
    if (selected) {
      setMessage("");
      removeSelectedItem(item.id);
      return;
    }
    if (
      selectionLimitReached
      || !allowedTypeSet.has(item.type)
      || !isPickerItemUsable(item, mode)
    ) return;
    setMessage("");
    replaceSelectedItem(item);
    if (!multiple) setLibraryOpen(false);
  };

  const detachCurrentItem = (itemId: string) => {
    if (itemId === pollingItemId) stopPolling();
    setMessage("");
    removeSelectedItem(itemId);
  };

  const deleteItem = async (item: KnowledgeItemData) => {
    const itemName = getKnowledgeItemDisplayName(item);
    const sourceDescription = item.type === "deep_report"
      ? "元の深掘りレポート"
      : item.type === "company_research"
      ? "元の企業リサーチ結果"
      : "元の社長メモやPDFの元データ";
    const confirmed = window.confirm(
      `「${itemName}」をナレッジライブラリから削除します。${sourceDescription}は削除されません。`
    );
    if (!confirmed) return;

    try {
      await api.deleteKnowledgeItem(item.id);
      setLibraryItems((current) => current.filter((entry) => entry.id !== item.id));
      if (selectedItems.some((entry) => entry.id === item.id)) {
        detachCurrentItem(item.id);
      }
    } catch (error) {
      logError(error);
      setMessage(`${toUserMessage(error)} 削除できませんでした。`);
    }
  };

  const defaultUsageNote = (
    <>
      <p>
        {allowsCompanyResearch
          ? `PDFやWebページ、社長メモ、企業リサーチ${reportSourceLabel}は、あなたの相談や分析に使用されます。`
          : allowsUrl
            ? "PDFやWebページ、社長メモは、あなたの相談や分析に使用されます。"
            : "アップロードしたPDFや社長メモは、あなたの相談に回答するために使用されます。"}
      </p>
      <p>
        他のユーザーに共有されることはありません。
        {multiple ? "ナレッジライブラリで管理・削除できます。" : "ナレッジはいつでも削除できます。"}
      </p>
      <p className="mt-1">
        ナレッジを読み取って要約する際には、その内容をAI処理に使用します。
        壁打ちの会話中は、相談に必要な要約や関連箇所を参照します。
      </p>
    </>
  );
  const renderedUsageNote = usageNote === undefined ? defaultUsageNote : usageNote;

  return (
    <section className={compact
      ? "mt-4 rounded-2xl border border-[#EADBC8] bg-[#FFFDFB] px-3 py-3"
      : "mt-5 rounded-[1.3rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-4"
    }>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-black text-[#5D4D46]">{renderedHeading}</h3>
          <p className="mt-1 text-sm leading-6 text-[#6B5851]">
            {renderedDescription}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {showUpload ? (
            <>
              <input
                ref={inputRef}
                aria-label="PDFをアップロード"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => void handleFileChange(event)}
                disabled={selectionLimitReached}
                className="sr-only"
              />
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={uploading || selectionLimitReached}
                className="min-h-[40px] rounded-full bg-[#5D4D46] px-4 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {uploading ? "アップロード中..." : multiple ? "PDFを追加" : "PDFをアップロード"}
              </button>
              {multiple ? (
                <span className="self-center text-xs font-bold text-[#8F7468]">
                  ナレッジライブラリに保存されます
                </span>
              ) : null}
            </>
          ) : null}
          <button
            type="button"
            onClick={() => void loadLibrary()}
            className="min-h-[40px] rounded-full border border-[#D9C5B9] bg-white px-4 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1]"
          >
            ナレッジライブラリから選ぶ
          </button>
        </div>
      </div>

      {multiple ? (
        <p className="mt-3 text-sm font-black text-[#6B5851]" aria-live="polite">
          {selectedItems.length} / {selectionLimit ?? "-"}件選択中
        </p>
      ) : null}

      {renderedUsageNote ? (
        <div className="mt-3 rounded-[1rem] bg-white px-3 py-3 text-xs leading-5 text-[#7B6861]">
          {renderedUsageNote}
        </div>
      ) : null}

      {selectedItems.length > 0 ? (
        <div className="mt-4 space-y-3" aria-label={selectedLabel}>
          {selectedItems.map((item) => {
            const currentName = getKnowledgeItemDisplayName(item);
            const currentFailure = item.status === "failed" ? getKnowledgeFailureDisplayMessage(item) : "";
            const currentUnavailableReason = !(mode === "groupTalk" && item.type === "url")
              ? getKnowledgeUnavailableReason(item)
              : "";
            const cannotUseCurrent = !allowedTypeSet.has(item.type) || !isPickerItemUsable(item, mode);
            return (
              <div key={item.id} className="rounded-[1.1rem] border border-[#EADBC8] bg-white p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-black text-[#A67C52]">{selectedLabel}</p>
                    <p className="mt-1 break-words text-sm font-black text-[#5D4D46] [overflow-wrap:anywhere]">{currentName}</p>
                    <p className="mt-1 text-sm font-bold text-[#6B5851]">種別: {getKnowledgeTypeLabel(item.type)}</p>
                    {item.type === "url" && item.domain ? (
                      <p className="mt-1 break-words text-xs font-bold text-[#8F7468] [overflow-wrap:anywhere]">{item.domain}</p>
                    ) : null}
                    <p className="mt-1 text-sm font-bold text-[#6B5851]">状態: {getPickerKnowledgeStatusLabel(item, mode)}</p>
                    {getKnowledgeStructureLabel(item.type, item.page_count) ? <p className="mt-1 text-xs font-bold text-[#8F7468]">{getKnowledgeStructureLabel(item.type, item.page_count)}</p> : null}
                    {shouldShowKnowledgeSummaryStatus(item) ? (
                      <p className="mt-1 text-xs font-bold text-[#8F7468]">{getKnowledgeSummaryStatusLabel(item)}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <button
                      type="button"
                      aria-label={multiple ? `選択解除 ${currentName}` : undefined}
                      onClick={() => detachCurrentItem(item.id)}
                      className="min-h-[40px] rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-xs font-black text-[#8F7468] transition hover:bg-[#FFF7F1]"
                    >
                      {detachLabel}
                    </button>
                    {deleteAllowed ? (
                      <button
                        type="button"
                        aria-label={`削除 ${currentName}`}
                        onClick={() => void deleteItem(item)}
                        className="min-h-[40px] rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-xs font-black text-[#8F7468] transition hover:bg-[#FFF7F1]"
                      >
                        削除
                      </button>
                    ) : null}
                  </div>
                </div>
                {isKnowledgeItemPollingStatus(item.status) ? (
                  <p className="mt-3 rounded-[0.9rem] bg-[#FFF7F1] px-3 py-2 text-sm font-bold text-[#8A6558]">{pollingMessage}</p>
                ) : null}
                {cannotUseCurrent ? (
                  <p className="mt-3 text-sm font-bold text-[#C06D5A]">
                    {currentFailure || currentUnavailableReason || unavailableFallbackMessage}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-4 rounded-[1rem] border border-dashed border-[#D9C5B9] bg-white px-3 py-3 text-sm font-bold text-[#8F7468]">
          {emptyMessage}
        </p>
      )}

      {message ? (
        <p role="alert" className="mt-3 rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-3 py-2 text-sm font-bold text-[#C06D5A]">
          {message}
        </p>
      ) : null}

      {libraryOpen ? (
        <div role="region" aria-label="ナレッジライブラリ選択" className="mt-4 flex max-h-[55vh] flex-col overflow-hidden rounded-[1.1rem] border border-[#EADBC8] bg-white p-4 sm:max-h-[480px]">
          <div className="sticky top-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 bg-white pb-3">
            <div><h4 className="text-sm font-black text-[#5D4D46]">ナレッジライブラリ</h4>
              {multiple && selectionLimit !== null && <p className="text-xs text-[#8F7468]">{selectedItems.length} / {selectionLimit}件選択中</p>}</div>
            <button
              type="button"
              onClick={() => setLibraryOpen(false)}
              className="rounded-full border border-[#EADBC8] px-3 py-1 text-xs font-black text-[#8F7468]"
            >
              {multiple ? "選択を終える" : "閉じる"}
            </button>
          </div>
          <div role="region" aria-label="選べるナレッジ一覧" tabIndex={0} className="min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
          {libraryLoading ? (
            <p className="mt-3 text-sm font-bold text-[#8F7468]">読み込み中...</p>
          ) : libraryItems.length === 0 ? (
            <p className="mt-3 text-sm font-bold text-[#8F7468]">ナレッジライブラリに表示できる項目はまだありません。</p>
          ) : (
            <div className="mt-3 space-y-3">
              {libraryItems.map((item) => {
                const usable = isPickerItemUsable(item, mode);
                const name = getKnowledgeItemDisplayName(item);
                const typeLabel = getKnowledgeTypeLabel(item.type);
                const selected = selectedItems.some((entry) => entry.id === item.id);
                const selectionDisabled = !usable || (!selected && selectionLimitReached);
                return (
                  <div
                    key={item.id}
                    className={`rounded-[1rem] border p-3 ${selected ? "border-[#D1907B] bg-[#FFF3EC]" : "border-[#EADBC8] bg-[#FFFDFB]"}`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-xs font-black text-[#A67C52]">{typeLabel}</p>
                        <p className="break-words text-sm font-black text-[#5D4D46]">{name}</p>
                        {item.type === "url" && item.domain ? (
                          <p className="mt-1 break-words text-xs font-bold text-[#8F7468] [overflow-wrap:anywhere]">
                            {item.domain}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs font-bold text-[#8F7468]">
                          状態: {getPickerKnowledgeStatusLabel(item, mode)}
                          {getKnowledgeStructureLabel(item.type, item.page_count) ? ` / ${getKnowledgeStructureLabel(item.type, item.page_count)}` : ""}
                          {shouldShowKnowledgeSummaryStatus(item)
                            ? ` / ${getKnowledgeSummaryStatusLabel(item)}`
                            : ""}
                        </p>
                        {item.summarized_at ? (
                          <p className="mt-1 text-xs text-[#8F7468]">要約: {formatDateTime(item.summarized_at)}</p>
                        ) : item.created_at ? (
                          <p className="mt-1 text-xs text-[#8F7468]">作成: {formatDateTime(item.created_at)}</p>
                        ) : null}
                        {item.status === "failed" ? (
                          <p className="mt-2 break-words text-xs font-bold text-[#C06D5A] [overflow-wrap:anywhere]">
                            {getKnowledgeFailureDisplayMessage(item)}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <button
                          type="button"
                          aria-label={`${selected ? "選択解除" : "選ぶ"} ${name}`}
                          aria-pressed={selected}
                          onClick={() => selectExistingItem(item)}
                          disabled={selectionDisabled}
                          className="min-h-[40px] rounded-full bg-[#5D4D46] px-3 py-1.5 text-xs font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {selected ? "選択済み" : "選ぶ"}
                        </button>
                        {deleteAllowed ? (
                          <button
                            type="button"
                            aria-label={`削除 ${name}`}
                            onClick={() => void deleteItem(item)}
                            className="min-h-[34px] rounded-full border border-[#EADBC8] bg-white px-3 py-1.5 text-xs font-black text-[#8F7468] transition hover:bg-[#FFF7F1]"
                          >
                            削除
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {!usable ? (
                      <p className="mt-2 text-xs font-bold text-[#8F7468]">
                        {getKnowledgeUnavailableReason(item) || "利用可能になったナレッジだけ選べます。"}
                      </p>
                    ) : !selected && selectionLimitReached ? (
                      <p className="mt-2 text-xs font-bold text-[#8F7468]">
                        {selectionLimit === null ? "選択条件を確認できません。" : `${selectionLimit}件まで選択できます。`}
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
          {libraryNextCursor && <button type="button" onClick={() => void loadMoreLibrary()} disabled={libraryLoading || libraryLoadingMore}
            className="mt-3 w-full rounded-full border border-[#EADBC8] px-3 py-2 text-sm font-bold disabled:opacity-50">
            {libraryLoadingMore ? "読み込み中..." : "さらに読み込む"}
          </button>}
          </div>
        </div>
      ) : null}
    </section>
  );
}
